// Shared client for every /api/admin/orderup/* endpoint (driver + restaurant
// application review) - one place for the x-admin-secret header pattern
// instead of duplicating fetch boilerplate per dashboard.
const API_BASE = 'https://api.starviewcinema.com';
export const SECRET_STORAGE_KEY = 'starviewcinema_admin_secret';

async function adminFetch(path, secret, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'x-admin-secret': secret,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((data && data.message) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

// ==================== Driver applications ====================

export function listDriverApplications(status, secret) {
  const params = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  return adminFetch(`/api/admin/orderup/driver-applications${params}`, secret).then((d) => d.applications || []);
}

export function getDriverApplication(id, secret) {
  return adminFetch(`/api/admin/orderup/driver-applications/${id}`, secret).then((d) => d.application);
}

// Document images are never a public URL (see aserver's driverDocumentsDir.ts) -
// the only way to read one back is this admin-gated endpoint, which needs the
// secret as a header. A plain <img src> can't attach custom headers, so this
// fetches the bytes itself and hands the browser a local blob: URL instead -
// the secret never appears in the image URL itself.
export async function fetchDocumentBlobUrl(applicationId, docKey, secret) {
  const res = await fetch(`${API_BASE}/api/admin/orderup/driver-applications/${applicationId}/documents/${docKey}`, {
    headers: { 'x-admin-secret': secret },
  });
  if (!res.ok) throw new Error(`Failed to load document "${docKey}" (${res.status})`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export function approveDriverApplication(id, reviewedBy, notes, secret) {
  return adminFetch(`/api/admin/orderup/driver-applications/${id}/approve`, secret, {
    method: 'PATCH',
    body: JSON.stringify({ reviewedBy, notes }),
  });
}

export function rejectDriverApplication(id, reviewedBy, reason, secret) {
  return adminFetch(`/api/admin/orderup/driver-applications/${id}/reject`, secret, {
    method: 'PATCH',
    body: JSON.stringify({ reviewedBy, reason }),
  });
}

// ==================== Restaurant applications ====================

export function listRestaurantApplications(status, secret) {
  const params = status && status !== 'all' ? `?status=${encodeURIComponent(status)}` : '';
  return adminFetch(`/api/admin/orderup/restaurant-applications${params}`, secret).then((d) => d.applications || []);
}

export function getRestaurantApplication(id, secret) {
  return adminFetch(`/api/admin/orderup/restaurant-applications/${id}`, secret).then((d) => d.application);
}

export function approveRestaurantApplication(id, reviewedBy, notes, secret) {
  return adminFetch(`/api/admin/orderup/restaurant-applications/${id}/approve`, secret, {
    method: 'PATCH',
    body: JSON.stringify({ reviewedBy, notes }),
  });
}

export function rejectRestaurantApplication(id, reviewedBy, reason, secret) {
  return adminFetch(`/api/admin/orderup/restaurant-applications/${id}/reject`, secret, {
    method: 'PATCH',
    body: JSON.stringify({ reviewedBy, reason }),
  });
}

// ==================== Reward fulfillment queue ====================
// Manual-fulfillment redemptions (trips, brand gift cards, external
// attraction tickets, merch): the customer already paid points and holds a
// confirmation code - each row here is a promise someone on the team still
// has to keep. Fulfill = you bought/sent the thing; Cancel = refunds the
// points and returns the unit to catalog inventory.

export function listRewardFulfillment(status, secret) {
  const params = `?status=${encodeURIComponent(status || 'pending')}&limit=200`;
  return adminFetch(`/api/admin/rewards/fulfillment${params}`, secret).then((d) => d.items || []);
}

export function completeRewardFulfillment(id, actor, note, secret) {
  return adminFetch(`/api/admin/rewards/fulfillment/${id}/complete`, secret, {
    method: 'POST',
    headers: { 'x-admin-actor': actor || 'admin' },
    body: JSON.stringify({ note }),
  });
}

export function cancelRewardFulfillment(id, actor, note, secret) {
  return adminFetch(`/api/admin/rewards/fulfillment/${id}/cancel`, secret, {
    method: 'POST',
    headers: { 'x-admin-actor': actor || 'admin' },
    body: JSON.stringify({ note }),
  });
}


// ==================== Screening Room (feature fleet) ====================
export function getFeatureFleetSummary(secret) {
  return adminFetch('/api/admin/movies/features/summary', secret);
}
export function listFeatureFleet({ status, country, search, limit } = {}, secret) {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (country) params.set('country', country);
  if (search) params.set('search', search);
  params.set('limit', String(limit || 60));
  return adminFetch(`/api/admin/movies/features/list?${params}`, secret);
}
export function queueFeatureEncode(movieId, sourceUrl, secret) {
  return adminFetch(`/api/admin/movies/${encodeURIComponent(movieId)}/encode-feature`, secret, {
    method: 'POST',
    body: JSON.stringify(sourceUrl ? { sourceUrl } : {}),
  });
}
export function bulkAttachFeatureSources(entries, secret) {
  return adminFetch('/api/admin/movies/features/bulk-source', secret, {
    method: 'POST',
    body: JSON.stringify({ entries }),
  });
}
export function getMasterUploadUrl(movieId, secret) {
  return adminFetch('/api/admin/movies/features/upload-url', secret, {
    method: 'POST',
    body: JSON.stringify({ movieId }),
  });
}
